import type { ComparisonCategory, PlaceSnapshot } from '@/types/gmb';

// 同業分類決策層（Batch 5-D1）
// deterministic：不呼叫 AI、不猜客戶產業，只根據 primaryType / primaryTypeDisplayName / types[] 判斷。
// 與 Backend find_google_place_peers 使用同一套規則（前端決定 UI 流程，後端再做一次防護）。

/** 實測過度寬泛、不可直接當同業分類的 primaryType。 */
export const GENERIC_COMPARISON_TYPES = new Set<string>(['consultant', 'service']);

/** 純地址／地理／廣義標籤，不可拿來當同業分類。 */
export const NON_COMPARISON_TYPES = new Set<string>([
  'establishment',
  'point_of_interest',
  'food',
  'health',
  'finance',
  'geocode',
  'premise',
  'route',
  'street_address',
  'political',
  'locality',
  'neighborhood',
  'sublocality',
  'plus_code',
]);

/** 是否為可用於同業比較的具體 Google type。 */
function isComparableType(type: string): boolean {
  return Boolean(type) && !GENERIC_COMPARISON_TYPES.has(type) && !NON_COMPARISON_TYPES.has(type);
}

/**
 * 由 Snapshot 決定同業分類：
 *   1) primaryType 具體 → GOOGLE_PRIMARY_TYPE
 *   2) primaryType 泛用或缺失 → 從 types[] 找更具體 type（保留 Google 原始順序）→ GOOGLE_TYPES_FALLBACK
 *   3) 仍不足 → USER_CONFIRMATION_REQUIRED（needsConfirmation = true，不執行 Peer Search）
 */
export function resolveComparisonCategory(snapshot: PlaceSnapshot): ComparisonCategory {
  const primaryType = typeof snapshot.primaryType === 'string' ? snapshot.primaryType.trim() : '';
  const displayName =
    typeof snapshot.primaryTypeDisplayName === 'string' ? snapshot.primaryTypeDisplayName.trim() : '';
  const types = Array.isArray(snapshot.types)
    ? snapshot.types.filter((t): t is string => typeof t === 'string' && t.length > 0)
    : [];

  if (isComparableType(primaryType)) {
    return {
      mode: 'GOOGLE_PRIMARY_TYPE',
      comparisonType: primaryType,
      comparisonLabel: displayName || primaryType,
      needsConfirmation: false,
    };
  }

  // primaryType 泛用或缺失 → 掃描 types[]，排除 primaryType 本身 / generic / broad，取第一個。
  const seen = new Set<string>();
  const candidate = types.find((type) => {
    if (type === primaryType) return false;
    if (!isComparableType(type)) return false;
    if (seen.has(type)) return false;
    seen.add(type);
    return true;
  });

  if (candidate) {
    return {
      mode: 'GOOGLE_TYPES_FALLBACK',
      comparisonType: candidate,
      comparisonLabel: candidate,
      needsConfirmation: false,
    };
  }

  return { mode: 'USER_CONFIRMATION_REQUIRED', needsConfirmation: true };
}

/** 建立使用者已確認的同業分類（USER_CONFIRMED）。 */
export function buildUserConfirmedCategory(keyword: string): ComparisonCategory {
  const trimmed = keyword.trim();
  return {
    mode: 'USER_CONFIRMED',
    comparisonKeyword: trimmed,
    comparisonLabel: trimmed,
    needsConfirmation: false,
  };
}